import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    const cartAction = this.querySelector('.action__cart');
    if (cartAction instanceof HTMLAnchorElement) {
      cartAction.addEventListener('click', this.#onCartClick, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount === undefined) return;

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };

  /**
   * Opens the AMP/Slide Cart app drawer from the header icon when the app is present.
   * @param {Event} event
   */
  #onCartClick = (event) => {
    const cartApi = /** @type {Window & { SLIDECART_OPEN?: () => void; AMP_API?: { OPEN_CART?: () => void } }} */ (
      window
    );

    if (typeof cartApi.SLIDECART_OPEN === 'function') {
      event.preventDefault();
      cartApi.SLIDECART_OPEN();
      return;
    }

    if (typeof cartApi.AMP_API?.OPEN_CART !== 'function') return;

    event.preventDefault();
    cartApi.AMP_API.OPEN_CART();
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
